// Mapa da Frota — todos os veículos com rastreador ativo, ao vivo (polling de
// 10 s, pausado com a aba oculta), inclusive os da diretoria, que fora de rota
// só aparecem aqui. Filtro por grupo e por veículo; ao selecionar um veículo,
// mostra o trajeto (até 7 dias) e a lista de paradas (até 31 dias) do período.
// Só o admin (fleet_tracking.read).
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Battery, Crown, MapPinned, RefreshCw, Route as RouteIcon, X } from 'lucide-react';
import { Button } from '@components/Button';
import { Badge, Card, CardContent } from '@components/Card';
import { DateInputBR } from '@components/DateInputBR';
import { PermissionGuard } from '@components/PermissionGuard';
import { vehicleTrackerService } from '@services/vehicleTrackerService';
import type { FleetLiveVehicle, VehicleStopsReport, VehicleTrack } from '../../types/vehicleTracker';
import { batteryVariant, formatAgo } from './trackerUtils';
import {
  FLEET_POLL_MS,
  STOPS_MAX_DAYS,
  TRACK_MAX_DAYS,
  filterByGroup,
  fleetSummary,
  formatClock,
  formatDayClock,
  formatDuration,
  formatKm,
  periodDays,
  periodPreset,
  todayISO,
  trackDistanceKm,
  vehicleMapState,
  vehicleName,
  vehicleStateInfo,
  type FleetGroup,
  type PeriodPreset,
} from './fleetMapUtils';
import { TrackLayer, VehicleMarker } from './fleetMapLayers';
import { usePolling } from './usePolling';

const SAO_PAULO: [number, number] = [-23.6, -46.7];
const GROUP_KEY = 'fleetMap.group';

const readGroup = (): FleetGroup => {
  try {
    const v = localStorage.getItem(GROUP_KEY);
    return v === 'delivery' || v === 'executive' ? v : 'all';
  } catch {
    return 'all';
  }
};

/** Enquadra os veículos na primeira carga e o trajeto quando ele muda. */
const FitOnce: React.FC<{ coords: [number, number][]; fitKey: string }> = ({ coords, fitKey }) => {
  const map = useMap();
  useEffect(() => {
    if (coords.length === 1) map.setView(coords[0], 15);
    else if (coords.length > 1) map.fitBounds(L.latLngBounds(coords), { padding: [40, 40], maxZoom: 16 });
    // só reenquadra quando a chave muda (não a cada atualização do polling)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitKey, map]);
  return null;
};

/** Centraliza o mapa numa parada clicada na tabela. */
const FlyTo: React.FC<{ target: [number, number] | null }> = ({ target }) => {
  const map = useMap();
  useEffect(() => {
    if (target) map.setView(target, 17);
  }, [target, map]);
  return null;
};

const errorMessage = (err: unknown, fallback: string) => (err instanceof Error ? err.message : fallback);

const GROUPS: { key: FleetGroup; label: string }[] = [
  { key: 'all', label: 'Todos' },
  { key: 'delivery', label: 'Entrega' },
  { key: 'executive', label: 'Diretoria' },
];

const PRESETS: { key: PeriodPreset; label: string }[] = [
  { key: 'today', label: 'Hoje' },
  { key: 'yesterday', label: 'Ontem' },
  { key: 'last7', label: '7 dias' },
];

const FleetMapContent: React.FC = () => {
  const [vehicles, setVehicles] = useState<FleetLiveVehicle[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [updatedAt, setUpdatedAt] = useState<string>('');
  const [group, setGroup] = useState<FleetGroup>(readGroup);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [from, setFrom] = useState(todayISO());
  const [to, setTo] = useState(todayISO());
  const [tab, setTab] = useState<'track' | 'stops'>('track');
  const [track, setTrack] = useState<VehicleTrack | null>(null);
  const [stops, setStops] = useState<VehicleStopsReport | null>(null);
  const [detailError, setDetailError] = useState<string | null>(null);
  const [flyTarget, setFlyTarget] = useState<[number, number] | null>(null);

  const today = todayISO();
  const days = periodDays(from, to);
  const periodValid = days >= 1;
  const trackAllowed = periodValid && days <= TRACK_MAX_DAYS;
  const stopsAllowed = periodValid && days <= STOPS_MAX_DAYS;
  const isLive = to === today;

  useEffect(() => {
    try {
      localStorage.setItem(GROUP_KEY, group);
    } catch {
      /* conveniência: sem storage, só não lembra */
    }
  }, [group]);

  const loadLive = useCallback(async () => {
    try {
      const data = await vehicleTrackerService.fleetLive();
      setVehicles(data);
      setError(null);
      setUpdatedAt(new Date().toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    } catch (err) {
      setError(errorMessage(err, 'Erro ao buscar a frota'));
    } finally {
      setLoaded(true);
    }
  }, []);

  const loadDetail = useCallback(async () => {
    if (selectedId == null || !periodValid) return;
    try {
      const [t, s] = await Promise.all([
        trackAllowed ? vehicleTrackerService.vehicleTrack(selectedId, from, to) : Promise.resolve(null),
        stopsAllowed ? vehicleTrackerService.vehicleStops(selectedId, from, to) : Promise.resolve(null),
      ]);
      setTrack(t);
      setStops(s);
      setDetailError(null);
    } catch (err) {
      setDetailError(errorMessage(err, 'Erro ao buscar o histórico'));
    }
  }, [selectedId, from, to, periodValid, trackAllowed, stopsAllowed]);

  usePolling(loadLive, FLEET_POLL_MS);
  // período que termina hoje acompanha o ao vivo; período passado é lido uma vez
  usePolling(loadDetail, FLEET_POLL_MS, selectedId != null && isLive);
  useEffect(() => {
    if (selectedId != null && !isLive) void loadDetail();
  }, [selectedId, isLive, loadDetail]);
  useEffect(() => {
    setTrack(null);
    setStops(null);
    setDetailError(null);
    setFlyTarget(null);
  }, [selectedId, from, to]);

  const visible = useMemo(() => filterByGroup(vehicles, group), [vehicles, group]);
  const summary = useMemo(() => fleetSummary(visible), [visible]);
  const selected = vehicles.find((v) => v.vehicle_id === selectedId) ?? null;
  const positioned = visible.filter((v) => v.latitude != null && v.longitude != null);
  const km = useMemo(() => (track ? trackDistanceKm(track.points) : 0), [track]);

  // veículo selecionado que saiu do filtro de grupo: fecha o painel
  useEffect(() => {
    if (selectedId != null && !visible.some((v) => v.vehicle_id === selectedId)) setSelectedId(null);
  }, [visible, selectedId]);

  const applyPreset = (p: PeriodPreset) => {
    const r = periodPreset(p, today);
    setFrom(r.from);
    setTo(r.to);
  };

  const fitCoords: [number, number][] =
    track && track.points.length > 0
      ? track.points.map((p) => [p.latitude, p.longitude])
      : selected?.latitude != null && selected.longitude != null
        ? [[selected.latitude, selected.longitude]]
        : positioned.map((v) => [v.latitude!, v.longitude!]);
  const fitKey = track
    ? `track-${selectedId}-${from}-${to}-${track.points.length > 0}`
    : selected
      ? `veh-${selectedId}`
      : `fleet-${group}-${positioned.length > 0}`;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-h1">Mapa da Frota</h1>
          <p className="text-slate-600">
            Posição dos veículos com rastreador, atualizada a cada 10 segundos{updatedAt ? ` (última às ${updatedAt})` : ''}
          </p>
        </div>
        <Button variant="secondary" onClick={() => void loadLive()} icon={<RefreshCw size={18} />}>
          Atualizar
        </Button>
      </div>

      {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}

      <div className="flex flex-col lg:flex-row lg:items-center gap-3">
        <div className="inline-flex rounded-lg border border-slate-300 overflow-hidden text-sm">
          {GROUPS.map((g) => (
            <button
              key={g.key}
              type="button"
              onClick={() => setGroup(g.key)}
              className={`px-3 py-1.5 ${group === g.key ? 'bg-violet-600 text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
            >
              {g.label}
            </button>
          ))}
        </div>
        <select
          className="px-3 py-1.5 border border-slate-300 rounded-lg text-sm min-w-[220px]"
          value={selectedId ?? ''}
          onChange={(e) => setSelectedId(e.target.value ? Number(e.target.value) : null)}
        >
          <option value="">Escolher veículo…</option>
          {visible.map((v) => (
            <option key={v.vehicle_id} value={v.vehicle_id}>
              {vehicleName(v)}
              {v.is_executive ? ' (diretoria)' : ''}
            </option>
          ))}
        </select>
        <div className="flex flex-wrap gap-2 text-sm">
          <Badge variant="success">{summary.moving} em movimento</Badge>
          <Badge variant="info">{summary.stopped} parados</Badge>
          <Badge variant="warning">{summary.silent} em silêncio</Badge>
          <Badge variant="danger">{summary.offline} sem comunicação</Badge>
          {summary.no_position > 0 && <Badge variant="neutral">{summary.no_position} sem posição</Badge>}
          <Badge variant="neutral">{summary.onRoute} em rota</Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[320px_1fr] gap-4">
        <Card>
          <CardContent className="p-0 max-h-[640px] overflow-auto divide-y divide-slate-100">
            {loaded && visible.length === 0 && (
              <div className="p-4 text-sm text-slate-500">
                {vehicles.length === 0 ? (
                  <>
                    Nenhum veículo com rastreador ativo. Vincule o rastreador no cadastro do veículo (Configuração → Transporte →{' '}
                    <Link to="/settings/transport/vehicles" className="text-blue-600 hover:underline">
                      Veículos
                    </Link>
                    ) ou em{' '}
                    <Link to="/settings/transport/trackers" className="text-blue-600 hover:underline">
                      Rastreadores
                    </Link>
                    .
                  </>
                ) : (
                  'Nenhum veículo neste grupo.'
                )}
              </div>
            )}
            {visible.map((v) => {
              const info = vehicleStateInfo(vehicleMapState(v));
              const active = v.vehicle_id === selectedId;
              return (
                <button
                  key={v.vehicle_id}
                  type="button"
                  onClick={() => setSelectedId(active ? null : v.vehicle_id)}
                  className={`w-full text-left p-3 hover:bg-slate-50 ${active ? 'bg-violet-50' : ''}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <span className="font-medium text-slate-900 flex items-center gap-1">
                      {v.is_executive && <Crown size={13} className="text-amber-600" aria-label="Diretoria" />}
                      {vehicleName(v)}
                    </span>
                    <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: info.color }} title={info.label} />
                  </div>
                  <div className="mt-1 flex flex-wrap items-center gap-1.5 text-xs">
                    <Badge variant={info.variant}>{info.label}</Badge>
                    {v.is_executive && (
                      <Badge variant={v.delivery_enabled ? 'info' : 'neutral'}>{v.delivery_enabled ? 'Diretoria · liberado' : 'Diretoria'}</Badge>
                    )}
                    {v.battery_percent != null && (
                      <Badge variant={batteryVariant(v.battery_percent)}>
                        <Battery size={11} className="inline mr-0.5" />
                        {v.battery_percent}%
                      </Badge>
                    )}
                    <span className="text-slate-500">{formatAgo(v.last_seen_seconds_ago)}</span>
                  </div>
                  {v.active_route && (
                    <div className="mt-1 text-xs text-slate-600 flex items-center gap-1">
                      <RouteIcon size={12} /> Rota #{v.active_route.driver_route_id}
                      {v.active_route.driver_name ? ` — ${v.active_route.driver_name}` : ''} ·{' '}
                      {v.active_route.total_stops - v.active_route.pending_stops}/{v.active_route.total_stops}
                    </div>
                  )}
                </button>
              );
            })}
          </CardContent>
        </Card>

        <div className="space-y-3">
          {selected && (
            <Card>
              <CardContent className="p-3 space-y-3">
                <div className="flex flex-col md:flex-row md:items-center gap-3">
                  <div className="flex-1 min-w-0 font-semibold flex items-center gap-2">
                    <MapPinned size={16} /> Histórico de {vehicleName(selected)}
                  </div>
                  <div className="flex flex-wrap items-center gap-2">
                    {PRESETS.map((p) => {
                      const r = periodPreset(p.key, today);
                      const on = r.from === from && r.to === to;
                      return (
                        <Button key={p.key} variant={on ? 'primary' : 'secondary'} size="sm" onClick={() => applyPreset(p.key)}>
                          {p.label}
                        </Button>
                      );
                    })}
                    <DateInputBR className="w-32 px-2 py-1.5 border border-slate-300 rounded-lg text-sm" value={from} onChange={(iso) => iso && setFrom(iso)} />
                    <span className="text-slate-500 text-sm">até</span>
                    <DateInputBR className="w-32 px-2 py-1.5 border border-slate-300 rounded-lg text-sm" value={to} onChange={(iso) => iso && setTo(iso)} />
                    <Button variant="secondary" size="sm" onClick={() => setSelectedId(null)} icon={<X size={16} />} title="Fechar histórico" />
                  </div>
                </div>

                {!periodValid && <div className="text-sm text-red-600">A data final precisa ser igual ou depois da inicial.</div>}
                {detailError && <div className="text-sm text-red-600">{detailError}</div>}

                <div className="flex gap-4 border-b border-slate-200 text-sm">
                  {(['track', 'stops'] as const).map((k) => (
                    <button
                      key={k}
                      type="button"
                      onClick={() => setTab(k)}
                      className={`pb-2 -mb-px border-b-2 ${tab === k ? 'border-violet-600 text-violet-700 font-medium' : 'border-transparent text-slate-500'}`}
                    >
                      {k === 'track' ? 'Trajeto' : `Paradas${stops ? ` (${stops.count})` : ''}`}
                    </button>
                  ))}
                </div>

                {tab === 'track' && (
                  <div className="text-sm text-slate-600">
                    {!trackAllowed && periodValid
                      ? `O trajeto no mapa é de até ${TRACK_MAX_DAYS} dias; reduza o período (a aba Paradas vai até ${STOPS_MAX_DAYS} dias).`
                      : track
                        ? track.points.length > 0
                          ? `${formatDayClock(track.points[0].at)} a ${formatDayClock(track.points[track.points.length - 1].at)} · ${formatKm(km)} · ${track.stops.length} paradas`
                          : 'Sem posições com GPS neste período'
                        : periodValid
                          ? 'Carregando trajeto…'
                          : ''}
                    {track?.sampled && ' · trajeto simplificado (muitas posições no período)'}
                    {track?.truncated && ' · período com posições demais, mostrando o início'}
                  </div>
                )}

                {tab === 'stops' && (
                  <div className="space-y-2">
                    {!stopsAllowed && periodValid && <div className="text-sm text-slate-600">A lista de paradas é de até {STOPS_MAX_DAYS} dias.</div>}
                    {stops && (
                      <>
                        <div className="text-sm text-slate-600">
                          {stops.count} paradas · {formatDuration(stops.total_seconds)} parado no período
                          {stops.truncated && ' · mostrando as primeiras 2.000'}
                        </div>
                        <div className="max-h-64 overflow-auto border border-slate-200 rounded-lg">
                          <table className="w-full text-sm">
                            <thead className="bg-slate-50 text-slate-600 sticky top-0">
                              <tr>
                                <th className="text-left px-3 py-2 font-medium">Início</th>
                                <th className="text-left px-3 py-2 font-medium">Fim</th>
                                <th className="text-left px-3 py-2 font-medium">Duração</th>
                                <th className="text-left px-3 py-2 font-medium">Local</th>
                              </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                              {stops.stops.length === 0 && (
                                <tr>
                                  <td colSpan={4} className="px-3 py-4 text-center text-slate-500">
                                    Nenhuma parada no período
                                  </td>
                                </tr>
                              )}
                              {stops.stops.map((s) => (
                                <tr
                                  key={`${s.start_at}-${s.latitude}`}
                                  className="hover:bg-violet-50 cursor-pointer"
                                  onClick={() => {
                                    setTab('track');
                                    setFlyTarget([s.latitude, s.longitude]);
                                  }}
                                  title="Mostrar no mapa"
                                >
                                  <td className="px-3 py-1.5">{formatDayClock(s.start_at)}</td>
                                  <td className="px-3 py-1.5">{s.ongoing ? <Badge variant="danger">parado agora</Badge> : formatClock(s.end_at)}</td>
                                  <td className="px-3 py-1.5">{formatDuration(s.duration_seconds)}</td>
                                  <td className="px-3 py-1.5">
                                    <a
                                      href={`https://www.google.com/maps?q=${s.latitude},${s.longitude}`}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="text-blue-600 hover:underline"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      {s.latitude.toFixed(5)}, {s.longitude.toFixed(5)}
                                    </a>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardContent className="p-0">
              <div style={{ height: 600 }}>
                <MapContainer center={SAO_PAULO} zoom={11} style={{ height: '100%', width: '100%', borderRadius: '0.75rem' }}>
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <FitOnce coords={fitCoords} fitKey={fitKey} />
                  <FlyTo target={flyTarget} />
                  {track && <TrackLayer points={track.points} stops={stops ? stops.stops : track.stops} />}
                  {positioned.map((v) => (
                    <VehicleMarker
                      key={v.vehicle_id}
                      vehicle={v}
                      selected={v.vehicle_id === selectedId}
                      onSelect={(veh) => setSelectedId(veh.vehicle_id)}
                    />
                  ))}
                </MapContainer>
              </div>
            </CardContent>
          </Card>
          <p className="text-xs text-slate-500">
            Cores: verde em movimento, azul parado, laranja em silêncio (o rastreador dorme com o veículo parado), cinza sem comunicação há
            mais de 24 h. No trajeto, laranja = parada, vermelho = parado agora. Veículos da diretoria (coroa) só aparecem nas rotas quando
            liberados para entregas no cadastro do veículo.
          </p>
        </div>
      </div>
    </div>
  );
};

export const FleetMapView: React.FC = () => (
  <PermissionGuard permission="fleet_tracking.read" showError>
    <FleetMapContent />
  </PermissionGuard>
);
